import React, { useState } from 'react';
import { AlertCircle, Check, HelpCircle } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';
import BandProfile from './BandProfile';
import BandSettings from './BandSettings';
import UserProfile from './UserProfile';
import BandMembers from './BandMembers';
import ContactFeedback from './ContactFeedback';
import DeleteBand from './DeleteBand';

export default function SettingsTab({ user, bandId, bandData, onShowWelcome }) {
  const [settingsMessage, setSettingsMessage] = useState('');
  const [error, setError] = useState('');
  const isMobile = useIsMobile();

  // Language detection for bilingual button label
  const language = localStorage.getItem('tapsody_language')?.startsWith('nl') ? 'nl' : 'en';

  // ── Shared helpers ──────────────────────────────────────
  const showMessage = (msg) => {
    setSettingsMessage(msg);
    setTimeout(() => setSettingsMessage(''), 3000);
  };

  const getBandMembers = () => {
    if (!bandData?.members) return [];
    return Object.keys(bandData.members).map(uid => ({
      uid,
      ...bandData.members[uid]
    })).sort((a, b) => {
      if (a.role === 'leader') return -1;
      if (b.role === 'leader') return 1;
      return new Date(a.joinedAt) - new Date(b.joinedAt);
    });
  };

  const isLeader = () => {
    if (!user || !bandData?.members) return false;
    return bandData.members[user.uid]?.role === 'leader';
  };

  return (
    <div className="space-y-6">
      {/* Tutorial pill button — top right */}
      <div className="flex justify-end">
        <button
          onClick={onShowWelcome}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-text-muted hover:text-white border border-surface-border-light hover:border-brand hover:bg-white/5 transition rounded-pill"
        >
          <HelpCircle className="w-4 h-4" />
          <span>{language === 'nl' ? 'Tutorial' : 'Tutorial'}</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-900/50 rounded-card p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1"><p className="text-red-100 text-sm">{error}</p></div>
          <button onClick={() => setError('')} className="text-red-300 hover:text-red-200">×</button>
        </div>
      )}

      {settingsMessage && (
        <div className="bg-green-900/50 rounded-card p-4 flex items-start gap-3">
          <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1"><p className="text-green-100 text-sm">{settingsMessage}</p></div>
          <button onClick={() => setSettingsMessage('')} className="text-green-300 hover:text-green-200">×</button>
        </div>
      )}

      <BandProfile
        user={user}
        bandId={bandId}
        bandData={bandData}
        setError={setError}
        showMessage={showMessage}
      />

      <BandSettings
        user={user}
        bandId={bandId}
        bandData={bandData}
        setError={setError}
        showMessage={showMessage}
      />

      <UserProfile
        user={user}
        bandId={bandId}
        bandData={bandData}
        setError={setError}
        showMessage={showMessage}
        isMobile={isMobile}
      />

      <BandMembers
        user={user}
        bandId={bandId}
        bandData={bandData}
        setError={setError}
        showMessage={showMessage}
        getBandMembers={getBandMembers}
        isLeader={isLeader}
      />

      <ContactFeedback
        user={user}
        bandId={bandId}
        bandData={bandData}
        setError={setError}
        showMessage={showMessage}
      />

      {isLeader() && (
        <DeleteBand
          user={user}
          bandId={bandId}
          bandData={bandData}
          setError={setError}
          getBandMembers={getBandMembers}
        />
      )}
    </div>
  );
}
